import { useState } from 'react';
import {
  IconArchive,
  IconChevronDown,
  IconLogout,
  IconTrash,
} from '@tabler/icons-react';
import {
  IconButton,
  Popover,
  RowActionPill,
  rowActionPillClasses,
  RowMenu,
  RowMenuDivider,
  RowMenuItem,
  WP_ICON_NAV,
} from '@wapharma/design-system';
import type { StaffSessionRow } from './StaffSessionsPanel';

function canRevokeSession(row: StaffSessionRow): boolean {
  return row.status === 'live' || row.status === 'active';
}

/**
 * Session row toggle — Déconnecter / Archiver (révoquer) / Supprimer.
 * Same RowActionPill chrome as rôles / clients.
 */
export function SessionRowActions({
  row,
  busy,
  onLogout,
  onArchive,
  onDelete,
}: {
  row: StaffSessionRow;
  busy?: boolean;
  onLogout: () => void;
  onArchive: () => void;
  onDelete: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const canRevoke = canRevokeSession(row);
  const label = row.isCurrent ? 'cette session (courante)' : 'cette session';

  return (
    <span
      data-datatable-rowclick-ignore
      onClick={(e) => e.stopPropagation()}
      className={rowActionPillClasses.wrap}
    >
      <RowActionPill
        allowOverflow
        disabled={busy}
        primary={
          <button
            type="button"
            aria-label={
              canRevoke
                ? `Déconnecter maintenant — ${label}`
                : `Supprimer — ${label}`
            }
            disabled={busy}
            onClick={(e) => {
              e.stopPropagation();
              if (canRevoke) onLogout();
              else onDelete();
            }}
            className={rowActionPillClasses.primaryBtn}
          >
            <span className={rowActionPillClasses.iconWrap}>
              {canRevoke ? (
                <IconLogout {...WP_ICON_NAV} aria-hidden />
              ) : (
                <IconTrash {...WP_ICON_NAV} aria-hidden />
              )}
            </span>
          </button>
        }
        menu={
          <Popover
            opened={menuOpen}
            onChange={setMenuOpen}
            portal
            align="end"
            portalMinWidth={220}
            portalEstimatedHeight={canRevoke ? 160 : 88}
            target={
              <IconButton
                type="button"
                size="xs"
                variant="ghost"
                aria-label={`Plus d’actions — ${label}`}
                aria-expanded={menuOpen}
                disabled={busy}
                className={rowActionPillClasses.menuBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen((v) => !v);
                }}
              >
                <IconChevronDown {...WP_ICON_NAV} aria-hidden />
              </IconButton>
            }
          >
            <RowMenu className={rowActionPillClasses.menuMin220}>
              {canRevoke ? (
                <>
                  <RowMenuItem
                    onClick={() => {
                      setMenuOpen(false);
                      onLogout();
                    }}
                  >
                    <span className={rowActionPillClasses.menuItemLabel}>
                      <IconLogout size={14} stroke={1.8} aria-hidden />
                      Déconnecter maintenant
                    </span>
                  </RowMenuItem>
                  <RowMenuItem
                    onClick={() => {
                      setMenuOpen(false);
                      onArchive();
                    }}
                  >
                    <span className={rowActionPillClasses.menuItemLabel}>
                      <IconArchive size={14} stroke={1.8} aria-hidden />
                      Archiver
                    </span>
                  </RowMenuItem>
                  <RowMenuDivider />
                </>
              ) : null}
              <RowMenuItem
                danger
                onClick={() => {
                  setMenuOpen(false);
                  onDelete();
                }}
              >
                <span className={rowActionPillClasses.menuItemLabel}>
                  <IconTrash size={14} stroke={1.8} aria-hidden />
                  Supprimer
                </span>
              </RowMenuItem>
            </RowMenu>
          </Popover>
        }
      />
    </span>
  );
}
